import React, { FC, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { updateCurrentDate } from '../../../../states/containers/common/currentSlice'
import { LangValue } from '../../../../states/types/common'
import { getGetPrams } from '../../../../utils/url'
import { dateManipulateDay, format, nowDate, toDate } from '../../../../utils/date'
import '../../../assets/scss/timeline_list.scss'
import { useTranslation } from 'react-i18next'
import { DateRange } from 'react-date-range'
import { ja, enUS } from 'date-fns/locale'
import { mapAllowedDateForTimeline } from '../../../../utils/plan'
import Search from '../../../components/risk/Search'
import { fetchRiskTimeline } from '../../../../states/containers/risk/riskSlice'
import { RequestGetRiskTimeline } from '../../../../states/types/risk'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Timeline from '../../../components/common/Timeline'
import Tips from '../../../components/common/Tips'

const PAGE_DISP_LIMIT = 5

interface GetParams {
  target_date: string
}

const TimelineList: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const {
    current,
    commonPart,
    risk,
  } = useSelector((state: RootState) => state)

  const [isDisplayFilter, setIsDisplayFilter] = useState<boolean>(false)

  const [targetDate, setTargetDate] = useState<string | undefined>(format(current.targetDate, 'YYYY-MM-DD'))

  const [isClickable, setIsClickable] = useState(true)

  const [selectPage, setSelectPage] = useState<number>(1)

  const [isReload, setIsReload] = useState<boolean>(false)

  useEffect(() => {
    const searchParams = location.search
    if (!searchParams) return
    if (!commonPart.company) return

    const getParams = getGetPrams(searchParams) as GetParams
    const targetDateParam = getParams.target_date || current.targetDate
    dispatch(updateCurrentDate(mapAllowedDateForTimeline(targetDateParam, commonPart.company.plan, commonPart.company.created_at)))
  }, [])

  useEffect(() => {
    if (!commonPart.company) return
    const searchParams = location.search
    const getParams = getGetPrams(searchParams) as GetParams
    const targetDateParam = getParams.target_date || current.targetDate
    const param:RequestGetRiskTimeline = { target_date: targetDateParam, page: selectPage, limit: PAGE_DISP_LIMIT }
    if (current.timelineSelectTeam) {
      param.team_ids = current.timelineSelectTeam.split(',').map((v) => Number(v))
    }
    if (current.timelineSelectOccupation) {
      param.occupation_ids = current.timelineSelectOccupation.split(',').map((v) => Number(v))
    }
    if (current.timelineSelectContract) {
      param.contract_keywords = current.timelineSelectContract.split(',')
    }
    if (current.timelineSelectPlace) {
      param.place_keywords = current.timelineSelectPlace.split(',')
    }
    dispatch(fetchRiskTimeline(param))
    setIsReload(false)
  }, [location.search, current, selectPage, isReload])

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const openCalendarModal = () => setIsCalendarDisplay(true)
  const closeCalendarModal = () => setIsCalendarDisplay(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeDate = (value: any) => {
    if (!commonPart.company) return
    let selectDate = format(value.selection.startDate, 'YYYY-MM-DD')
    if (current.targetDate === selectDate) {
      selectDate = format(value.selection.endDate, 'YYYY-MM-DD')
    }
    setTargetDate(selectDate)
    closeCalendarModal()
    setIsClickable(false)
    dispatch(updateCurrentDate(mapAllowedDateForTimeline(format(selectDate, 'YYYY-MM-DD'), commonPart.company.plan, commonPart.company.created_at)))
    history.push(`?target_date=${format(selectDate, 'YYYY-MM-DD')}`)
  }

  const toggleFilter = () => {
    setIsDisplayFilter(!isDisplayFilter)
  }

  useEffect(() => {
    setTimeout(() => {
      setIsClickable(true)
    }, 3000)
  }, [isClickable])

  useEffect(() => {
    setTargetDate(format(current.targetDate, 'YYYY-MM-DD'))
  }, [current.targetDate])

  return (
    <section className="risk_page">
      <Helmet>
        <title>{`Qasee - リスクアセスメント - ${t('timeline_list.index.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        リスクアセスメント
        &nbsp;&gt;&nbsp;
        {t('timeline_list.index.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1 className="question">
          {t('timeline_list.index.title')}
        </h1>
        <div className="tools">
          <div
            style={{
              position: "relative",
            }}
          >
            <Tips
              description="最新の情報に更新"
              className=""
              icon={false}
            >
              <>
                <button
                  type="button"
                  onClick={() => setIsReload(true)}
                  disabled={risk.isLoading}
                  style={{
                    border: "1px solid #cccccc",
                    borderRadius: "5px",
                    position: "relative",
                    fontSize: "14px",
                    padding: "8px 12px",
                    color: "#666666",
                    margin: 0,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                </button>
              </>
            </Tips>
          </div>
          <div className="calendar_text">
            <button
              type="button"
              className="display button"
              onClick={
                () => handleChangeDate(
                  {
                    selection: {
                      startDate: nowDate("YYYY-MM-DD"),
                      endDate: nowDate("YYYY-MM-DD"),
                    },
                  },
                )
              }
              disabled={!isClickable}
              style={{
                borderRadius: "24px",
                fontSize: "12px",
              }}
            >
              今日
            </button>
            <button
              type="button"
              className="display button"
              onClick={() => openCalendarModal()}
              disabled={!isClickable}
            >
              {format(current.targetDate, t('time.date.formats.with_weekday'), language)}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
            </button>
            <button
              type="button"
              className="prev button"
              onClick={
                () => handleChangeDate(
                  {
                    selection: {
                      startDate: dateManipulateDay(current.targetDate, -1, 'YYYY-MM-DD'),
                      endDate: dateManipulateDay(current.targetDate, -1, 'YYYY-MM-DD'),
                    },
                  },
                )
              }
              disabled={format(current.targetDate) === format(commonPart.company?.created_at) || !isClickable}
            >
              <FontAwesomeIcon icon={['fas', 'angle-left']} className="icon" />
            </button>
            <button
              type="button"
              className="next button"
              onClick={
                () => handleChangeDate(
                  {
                    selection: {
                      startDate: dateManipulateDay(current.targetDate, 1, 'YYYY-MM-DD'),
                      endDate: dateManipulateDay(current.targetDate, 1, 'YYYY-MM-DD'),
                    },
                  },
                )
              }
              disabled={format(current.targetDate) === nowDate() || !isClickable}
            >
              <FontAwesomeIcon icon={['fas', 'angle-right']} className="icon" />
            </button>
            {
              isCalendarDisplay && (
                <>
                  <div className="calendar_box">
                    <div className="daily">
                      <DateRange
                        locale={language === 'en' ? enUS : ja}
                        ranges={[{ startDate: toDate(targetDate), endDate: toDate(targetDate), key: 'selection' }]}
                        onChange={(value) => handleChangeDate(value)}
                        maxDate={toDate(nowDate())}
                        minDate={toDate(commonPart.company?.created_at)}
                        showDateDisplay={false}
                        dragSelectionEnabled={false}
                        showPreview={false}
                        moveRangeOnFirstSelection
                        weekStartsOn={0}
                      />
                    </div>
                  </div>
                  <div className="screen transparent" role="presentation" onClick={closeCalendarModal} />
                </>
              )
            }
          </div>
          <button
            type="button"
            onClick={toggleFilter}
            className="filter_button"
          >
            <FontAwesomeIcon icon={['fas', 'filter']} className="icon" />
            {
              isDisplayFilter ? t('dashboard.search.filter_close') : t('dashboard.search.filter_open')
            }
          </button>
        </div>
      </section>
      <section className="page_description">
        <p>
          タイムラインでは各ユーザーの時間経過に沿った入力量の推移や、各時間帯における使用割合の高いアプリを知ることができます。
        </p>
        <p>
          bizとは、キーボードやマウス移動による「PC操作量」を表します。係数をかけて弊社独自の計算式を当てはめ、数値化したものになります。
        </p>
        <p>
          一概に「bizが高いから優秀である」「bizが低いから改善すべきポイントがある」と言い切ることはできませんが、数値からどのようなタイプの作業を行っているか推察可能です。
        </p>
      </section>
      <section className="content-filter">
        <Search
          isDisplayFilter={isDisplayFilter}
          setIsDisplayFilter={setIsDisplayFilter}
        />
      </section>
      <section className="page_contents content-area clear">
        <section className="content">
          {
            risk.isLoading ? (
              <LoadingSquare>
                <>
                  タイムライン情報を取得しています…
                </>
              </LoadingSquare>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "16px",
                  }}
                >
                  {
                    risk.riskTimeline?.members.map((v) => (
                      <React.Fragment key={`timeline_${v.name}`}>
                        <Timeline
                          member={v}
                          targetDate={targetDate || nowDate()}
                          maxValue={risk.riskTimeline?.max_action_value}
                        />
                      </React.Fragment>
                    ))
                  }
                </div>
                <div className="row">
                  <div className="box paging-box">
                    <div className="col-md-12 paging">
                      {
                        selectPage > 1 && (
                          <button
                            type="button"
                            className="pagination"
                            onClick={() => setSelectPage(selectPage - 1)}
                          >
                            &lt;
                          </button>
                        )
                      }
                      {
                        Array(risk.riskTimeline?.page_count).fill(0).map((_v, i) => (
                          <button
                            type="button"
                            key={`page_${i + 0}`}
                            className={`pagination ${selectPage === i + 1 ? 'active' : ''}`}
                            onClick={() => setSelectPage(i + 1)}
                          >
                            {i + 1}
                          </button>
                        ))
                      }
                      {
                        (risk.riskTimeline?.page_count || 1) > selectPage && (
                          <button
                            type="button"
                            className="pagination"
                            onClick={() => setSelectPage(selectPage + 1)}
                          >
                            &gt;
                          </button>
                        )
                      }
                    </div>
                  </div>
                </div>
              </>
            )
          }
        </section>
      </section>
    </section>
  )
}

export default TimelineList
